import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { PremiumDiscountBoxes } from '@components/features/cards/employer/PremiumDiscountBoxes';
import { CheckIcon } from '@heroicons/react/16/solid';
import { Button } from '@components/common';
import { useCompanyJobActions } from '@components/templates/my-jobs/hooks/useCompanyJobActions';
import { JobFolderSelector } from '@components/templates/cv/partials/JobFolderSelector';
import React, { useEffect, useState } from 'react';
import { OperationJob } from '@type/v1-api-types';
import { useSwrResumeGetOperationJob } from '@hooks/useSwrApi/endpoints/resume-operations';


interface PremiumModalProps {
  locale: Locale;
  jobSlug: string;
  code: number;
}

/**
 * @description Renders the cv view limit exceeded modal
 *
 * The modal can be opened anywhere in the app using the **showPremiumModal** function.
 */
export const PremiumModal = NiceModal.create((props: PremiumModalProps) => {
  const { locale, jobSlug, code } = props;

  const modal = useModal();

  const [activeJobContext, setActiveJobContext] = useState<OperationJob>({
    jobId: 0,
    title: '',
    jobSlug: jobSlug ?? '',
  });

  // Translation
  const { t } = useTranslation('common');


  const {
    data: accountDiscountData,
  } = useSwrAccountDiscountData({
    locale,
  });

  const {
    trigger: getOperationJobTrigger,
  } = useSwrResumeGetOperationJob({
    locale: 'ro',
    successCallback: (operationJobData) => {
      setActiveJobContext(operationJobData);
    },
  });

  useEffect(() => {
    void getOperationJobTrigger();
  }, [getOperationJobTrigger]);


  // Add job is allowed only for not logged or company users. We need this to check if we need to switch account
  // const headerInfo = useAppSelector((state) => state.user.headerInfo);

  const closeModal = () => {
    void modal.hide();
  };

  const {
    handleSearchpackConfirm,
    isActivatingSearchpack,
  } = useCompanyJobActions({
    jobSlug: activeJobContext?.jobSlug ?? '',
    jobId: Number(activeJobContext?.jobId) ?? '',
    jobTitle: activeJobContext?.title ?? '',
    currentJobState: 'active',
    onSuccesSearchpackActivation: () => {
      void modal.hide();
      window.location.reload();
    },
  });


  // Render component
  // *****************************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      backdropClickDisabled
      size="lg"
    >
      <Modal.Header onClose={closeModal}>
        <h3>
          {code === 804
          && t('error.view-limit.missing-searchpack')}
          {code === 805
          && t('error.view-limit.subscription-quota')}
        </h3>
      </Modal.Header>
      <Modal.Body>
        <div className="items-end justify-center md:flex">
          <div>
            <PremiumDiscountBoxes
              showOnlyNext
              currentDiscountPercentage={accountDiscountData?.currentDiscountPercentage}
              isAuth
            />
          </div>

          <div className="items-center justify-between md:flex">
            <div className="m-5 text-center md:my-0 md:text-left">{t('global.label.or.separator')}</div>

            <div>
              <h5 className="mb-3 text-md font-bold">{t('my-jobs.card.actions.searchpack')}</h5>
              <div className="rounded-md border p-4">
                <div
                  className="mb-5 rounded-lg border border-dashed border-secondary bg-surface-100 px-3 py-2 shadow-lg"
                >
                  <JobFolderSelector
                    selectedJobFolder={{
                      jobId: Number(activeJobContext?.jobId) ?? 0,
                      title: activeJobContext?.title ?? '',
                      jobSlug: activeJobContext?.jobSlug ?? '',
                    }}
                    sendJobFolderInfoToParent={(item) => {
                      setActiveJobContext(item);
                    }}
                  />
                </div>

                <p className="font-bold">
                  {' '}
                  {t('global.action.searchpack.details.included.benefits')}
                </p>
                <div className="my-5 flex items-center gap-5">
                  <div className="border-r-2 border-gray-500/20 pr-5">
                    <div className=" flex items-center gap-2">
                      <CheckIcon className="size-5 text-secondary" />
                      <span>{t('global.searchpack.benefit.2')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckIcon className="size-5 text-secondary" />
                      <span>{t('global.searchpack.benefit.1')}</span>
                    </div>
                  </div>

                  <div className="font-bold">
                    100 neuro
                  </div>
                </div>
                <p>{t('global.action.searchpack.details')}</p>
                <Button
                  color="secondary"
                  onClick={handleSearchpackConfirm}
                  isLoading={isActivatingSearchpack}
                  size="sm"
                  className="mt-5 rounded-full"
                >
                  {t('global.action.searchpack.activate')}
                </Button>
              </div>
            </div>

          </div>

        </div>

      </Modal.Body>
      <Modal.Footer className="text-center">
        <Button onClick={() => closeModal()} color="light" size="sm">
          {t('global.action.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});


/**
 * @description Opens the cv view limit modal
 *
 * @example
 * // import the function
 * import { showPremiumModal } from '@components/features/modals/employer/CvViewLimitExceeded';
 * // usage
 * <Button onClick={showPremiumModal()}>Show modal</Button>
 */
export const showPremiumModal = (locale: Locale, jobSlug: string, code: number) => {
  void NiceModal.show(PremiumModal, { locale, jobSlug, code });
};
